import type { RouterHistory } from '@tanstack/react-router';
import { createRouter } from '@tanstack/react-router';
import { queryClient } from '@/shared/api/query-client';
import { protectedRoute, rootRoute } from './routes/_root';
// ─── Auth ────────────────────────────────────────────────────────
import {
  embedWidgetRoute,
  forgotPasswordRoute,
  profileIndexRoute,
  profileOrganizationRoute,
  profilePersonalRoute,
  profileRoute,
  profileSecurityRoute,
  signInRoute,
  signUpRoute,
} from './routes/auth';
// ─── CMS ─────────────────────────────────────────────────────────
import {
  cmsCatalogTranslateRoute,
  cmsHashtagAssociationsRoute,
  cmsItinerariesRoute,
  cmsLandingPageEditorRoute,
  cmsLandingPagesRoute,
  cmsMarketingRegenerateRoute,
  cmsMetaInfoRoute,
  cmsPagesRoute,
  cmsProductDetailRoute,
  cmsProductsRoute,
  cmsSettingsRoute,
  cmsStaticPageEditorRoute,
  cmsStaticPagesRoute,
  cmsTaxonomyRoute,
} from './routes/cms';
// ─── CRM ─────────────────────────────────────────────────────────
import {
  crmAccountEditRoute,
  crmAccountNewRoute,
  crmAccountsRoute,
  crmContactDetailRoute,
  crmContactEditRoute,
  crmContactNewRoute,
  crmContactsRoute,
  crmDriverEditRoute,
  crmDriverNewRoute,
  crmDriversRoute,
  crmDuplicatesRoute,
  crmGuideEditRoute,
  crmGuideNewRoute,
  crmGuidesRoute,
  crmLayoutsRoute,
  crmLogsRoute,
  crmOrphanedCustomersRoute,
  crmRoute,
} from './routes/crm';
// ─── Customer discussion (public standing link) ──────────────
import { customerPreviewDiscussionRoute } from './routes/customer-preview';
// ─── Dashboard ───────────────────────────────────────────────────
import {
  consultantDashboardRoute,
  consultantMoodboardRoute,
  managementSalesGoalsRoute,
} from './routes/dashboard';
import { finalClientFormRoute } from './routes/final-client-form';
// ─── Hyperguest ──────────────────────────────────────────────────
import {
  managementHyperguestMapperRoute,
  managementHyperguestSearchRoute,
} from './routes/hyperguest';
// ─── Inbox ───────────────────────────────────────────────────────
import { inboxNewRoute, inboxRoute, inboxThreadRoute } from './routes/inbox';
// ─── Operations ──────────────────────────────────────────────────
import { incidentsManagementRoute } from './routes/incidents';
// ─── Itineraries ─────────────────────────────────────────────────
import {
  advisorPlannerRoute,
  itinerariesCreateRoute,
  itinerariesRoute,
  itineraryDetailRoute,
  itineraryItemEditRoute,
  itineraryPricesRoute,
  itinerarySettingsRoute,
  managementBulkStatusChangeRoute,
  managementEmbedGeneratorRoute,
  managementPaymentLogsRoute,
  paymentsRoute,
} from './routes/itineraries';
// ─── Marketing HUB (XT-808) ──────────────────────────────────────
import {
  marketingActionDetailRoute,
  marketingActionsRoute,
  marketingDashboardRoute,
  marketingMapRoute,
  marketingOverheadsRoute,
  marketingRoute,
  marketingSettingsRoute,
  marketingTasksRoute,
} from './routes/marketing';
// ─── Misc (index, optigest, sandbox, 404) ────────────────────────
import {
  helpRoute,
  indexRoute,
  itineraryBuilderEvalRoute,
  managementOptigestRoute,
  managementProductApiRoute,
  notFoundRoute,
  sandboxAgentPanelRoute,
  systemBriefRoute,
} from './routes/misc';
import { operationsRoute } from './routes/operations';
// ─── Ops Dashboard ───────────────────────────────────────────────
import {
  operationsDashboardRoute,
  operationsDashboardV2Route,
} from './routes/ops-dashboard';
// ─── Component playground (dev-only; gated out of prod builds) ────
import {
  playgroundComponentRoute,
  playgroundIndexRoute,
} from './routes/playground';
// ─── Preview (public) ────────────────────────────────────────────
import {
  checkoutCancelRoute,
  checkoutRoute,
  checkoutSuccessRoute,
  operationPreviewActionRoute,
  operationPreviewDetailsRoute,
  previewHomeRoute,
  previewItineraryRoute,
  previewLayoutRoute,
  previewMapRoute,
  previewOptionalsRoute,
  previewStaysRoute,
  pvAliasRoute,
  pvAliasShortRoute,
} from './routes/preview';
// ─── Products ────────────────────────────────────────────────────
import {
  autoTranslateCatalogRoute,
  customRegionsRoute,
  productDetailRoute,
  productEditLegacyTabRoutes,
  productEditRoute,
  productsRoute,
} from './routes/products';
// ─── Recommendations ─────────────────────────────────────────────
import { managementRecommendationsRoute } from './routes/recommendations';
// ─── Settings / Config ───────────────────────────────────────────
import {
  apiMonitoringIndexRoute,
  apiMonitoringManualRoute,
  apiMonitoringMonitorRoute,
  apiMonitoringRoute,
  exportConfigurationIndexRoute,
  exportConfigurationProductsRoute,
  exportConfigurationRoute,
  inconsistencyResolverRoute,
  partnerConfigurationRoute,
  pricingConfigDefaultPricingRoute,
  pricingConfigExchangeRateRoute,
  pricingConfigPricingTypeRoute,
  pricingConfigurationIndexRoute,
  pricingConfigurationRoute,
  pricingConfigVatRoute,
  statusFlowActionLogsRoute,
  statusFlowActionsRoute,
  statusFlowIndexRoute,
  statusFlowItineraryRoute,
  statusFlowOperationsCatalogRoute,
  statusFlowOperationsRoute,
  statusFlowRoute,
  systemConfigBudgetSettingsRoute,
  systemConfigIndexRoute,
  systemConfigIntegrationsRoute,
  systemConfigLayoutTemplatesRoute,
  systemConfigOperationsStatusRoute,
  systemConfigOrganizationRoute,
  systemConfigPreviewFollowUpRoute,
  systemConfigPricingRoute,
  systemConfigRoute,
  systemConfigStatusActionLogsRoute,
  systemConfigStatusActionsRoute,
  systemConfigStatusProcessRoute,
  systemConfigTermsAndConditionsRoute,
  systemConfigTicketLabelsRoute,
  systemConfigTicketVenuesRoute,
  systemConfigTravellerTypesRoute,
  systemConfigVideoSuggestionsRoute,
  tasksManagementCollectionsRoute,
  tasksManagementIndexRoute,
  tasksManagementRoute,
  tasksManagementTasksRoute,
  userSettingsAccountsRoute,
  userSettingsIndexRoute,
  userSettingsRolesRoute,
  userSettingsRoute,
  userSettingsTeamsRoute,
} from './routes/settings';
// ─── Support ─────────────────────────────────────────────────────
import { supportManagementRoute } from './routes/support';
// ─── Messaging (WhatsApp) ────────────────────────────────────────
import {
  messagingDefaultTemplatesRoute,
  messagingExpireRulesRoute,
  messagingIndexRoute,
  messagingProcessActionsRoute,
  messagingRoute,
  messagingSentTimeRulesRoute,
  messagingSettingsRoute,
  messagingTemplatesRoute,
} from './routes/whatsapp';

// ─── Route tree ──────────────────────────────────────────────────

const routeTree = rootRoute.addChildren([
  signInRoute,
  signUpRoute,
  forgotPasswordRoute,
  embedWidgetRoute,
  checkoutRoute,
  checkoutSuccessRoute,
  checkoutCancelRoute,
  previewLayoutRoute.addChildren([
    previewHomeRoute,
    previewItineraryRoute,
    previewMapRoute,
    previewStaysRoute,
    previewOptionalsRoute,
  ]),
  pvAliasRoute,
  pvAliasShortRoute,
  operationPreviewDetailsRoute,
  operationPreviewActionRoute,
  customerPreviewDiscussionRoute,
  finalClientFormRoute,
  protectedRoute.addChildren([
    indexRoute,
    helpRoute,
    profileRoute.addChildren([
      profileIndexRoute,
      profilePersonalRoute,
      profileSecurityRoute,
      profileOrganizationRoute,
    ]),
    crmRoute,
    crmAccountsRoute,
    crmAccountNewRoute,
    crmAccountEditRoute,
    crmContactsRoute,
    crmContactNewRoute,
    crmContactDetailRoute,
    crmContactEditRoute,
    crmDriversRoute,
    crmDriverNewRoute,
    crmDriverEditRoute,
    crmGuidesRoute,
    crmGuideNewRoute,
    crmGuideEditRoute,
    crmLayoutsRoute,
    crmLogsRoute,
    crmDuplicatesRoute,
    crmOrphanedCustomersRoute,
    marketingRoute,
    marketingActionsRoute,
    marketingActionDetailRoute,
    marketingMapRoute,
    marketingTasksRoute,
    marketingDashboardRoute,
    marketingOverheadsRoute,
    marketingSettingsRoute,
    consultantDashboardRoute,
    consultantMoodboardRoute,
    operationsRoute,
    incidentsManagementRoute,
    supportManagementRoute,
    operationsDashboardRoute,
    operationsDashboardV2Route,
    tasksManagementRoute.addChildren([
      tasksManagementIndexRoute,
      tasksManagementTasksRoute,
      tasksManagementCollectionsRoute,
    ]),
    userSettingsRoute.addChildren([
      userSettingsIndexRoute,
      userSettingsAccountsRoute,
      userSettingsTeamsRoute,
      userSettingsRolesRoute,
    ]),
    systemConfigRoute.addChildren([
      systemConfigIndexRoute,
      systemConfigOrganizationRoute,
      systemConfigStatusActionLogsRoute,
      systemConfigStatusActionsRoute,
      systemConfigStatusProcessRoute,
      systemConfigIntegrationsRoute,
      systemConfigPreviewFollowUpRoute,
      systemConfigBudgetSettingsRoute,
      systemConfigPricingRoute,
      systemConfigOperationsStatusRoute,
      systemConfigTravellerTypesRoute,
      systemConfigLayoutTemplatesRoute,
      systemConfigTicketVenuesRoute,
      systemConfigTicketLabelsRoute,
      systemConfigVideoSuggestionsRoute,
      systemConfigTermsAndConditionsRoute,
    ]),
    statusFlowRoute.addChildren([
      statusFlowIndexRoute,
      statusFlowItineraryRoute,
      statusFlowOperationsRoute,
      statusFlowActionsRoute,
      statusFlowActionLogsRoute,
      statusFlowOperationsCatalogRoute,
    ]),
    partnerConfigurationRoute,
    messagingRoute.addChildren([
      messagingIndexRoute,
      messagingTemplatesRoute,
      messagingDefaultTemplatesRoute,
      messagingExpireRulesRoute,
      messagingSentTimeRulesRoute,
      messagingProcessActionsRoute,
      messagingSettingsRoute,
    ]),
    pricingConfigurationRoute.addChildren([
      pricingConfigurationIndexRoute,
      pricingConfigPricingTypeRoute,
      pricingConfigDefaultPricingRoute,
      pricingConfigVatRoute,
      pricingConfigExchangeRateRoute,
    ]),
    productsRoute,
    managementSalesGoalsRoute,
    managementRecommendationsRoute,
    managementPaymentLogsRoute,
    managementBulkStatusChangeRoute,
    managementOptigestRoute,
    managementProductApiRoute,
    systemBriefRoute,
    managementEmbedGeneratorRoute,
    itineraryBuilderEvalRoute,
    paymentsRoute,
    managementHyperguestMapperRoute,
    managementHyperguestSearchRoute,
    apiMonitoringRoute.addChildren([
      apiMonitoringIndexRoute,
      apiMonitoringMonitorRoute,
      apiMonitoringManualRoute,
    ]),
    inconsistencyResolverRoute,
    autoTranslateCatalogRoute,
    exportConfigurationRoute.addChildren([
      exportConfigurationIndexRoute,
      exportConfigurationProductsRoute,
    ]),
    customRegionsRoute,
    productDetailRoute,
    productEditRoute.addChildren(productEditLegacyTabRoutes),
    cmsProductsRoute,
    cmsProductDetailRoute,
    cmsItinerariesRoute,
    cmsPagesRoute,
    cmsTaxonomyRoute,
    cmsLandingPagesRoute,
    cmsLandingPageEditorRoute,
    cmsStaticPagesRoute,
    cmsStaticPageEditorRoute,
    cmsHashtagAssociationsRoute,
    cmsMetaInfoRoute,
    cmsCatalogTranslateRoute,
    cmsMarketingRegenerateRoute,
    cmsSettingsRoute,
    inboxRoute,
    inboxNewRoute,
    inboxThreadRoute,
    sandboxAgentPanelRoute,
    itinerariesRoute,
    itineraryPricesRoute,
    itinerariesCreateRoute,
    advisorPlannerRoute,
    itineraryDetailRoute,
    itinerarySettingsRoute,
    itineraryItemEditRoute,
  ]),
  ...(playgroundIndexRoute ? [playgroundIndexRoute] : []),
  ...(playgroundComponentRoute ? [playgroundComponentRoute] : []),
  notFoundRoute,
]);

// Factory so tests (apps/web/src/shared/test/render-app.tsx) can mount the same
// route tree on an in-memory history without touching the prod singleton below.
export const buildRouter = (history?: RouterHistory) =>
  createRouter({
    routeTree,
    defaultPreload: 'intent',
    context: { queryClient },
    ...(history ? { history } : {}),
  });

export const router = buildRouter();

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
